import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartItemsComponent } from '@theme/component-cart-items';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Posterboyz: adds a sticker pack to the cart from the cart drawer or cart page,
 * then lets the surrounding cart-items-component re-render from the returned sections.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [error] - Inline error message.
 *
 * @extends {Component<Refs>}
 */
class CartStickerAddon extends Component {
  #busy = false;
  /** @type {WeakSet<Element>} */
  #wiredSliders = new WeakSet();
  /** @type {WeakMap<Element, number>} last slide index per slider, so opening doesn't announce "slide 1" */
  #lastIndex = new WeakMap();

  /**
   * A live region outside the re-rendered cart markup, so "added" survives the morph.
   * @param {string} text
   */
  static announce(text) {
    let region = document.getElementById('sticker-addon-status');
    if (!region) {
      region = document.createElement('p');
      region.id = 'sticker-addon-status';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.append(region);
    }
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = text));
  }

  /**
   * @param {number | string} packId
   * @returns {HTMLDialogElement | null}
   */
  #previewFor(packId) {
    return this.querySelector(`dialog.sticker-preview[data-pack-id="${packId}"]`);
  }

  /** @returns {HTMLDialogElement | null} */
  get #openPreview() {
    return this.querySelector('dialog.sticker-preview[open]');
  }

  /** @param {number} packId */
  openPreview(packId) {
    const dialog = this.#previewFor(packId);
    if (!dialog || dialog.open) return;

    const slides = dialog.querySelector('[data-slides]');
    if (slides && !this.#wiredSliders.has(slides)) {
      this.#wiredSliders.add(slides);
      slides.addEventListener('scroll', () => this.#updateSlider(dialog), { passive: true });
    }

    dialog.showModal();
    slides?.scrollTo({ left: 0 });
    if (slides) this.#lastIndex.delete(slides);
    this.#updateSlider(dialog);
  }

  closePreview() {
    this.#openPreview?.close();
  }

  /**
   * Closes the card when the backdrop (outside the card) is clicked.
   * @param {MouseEvent} event
   */
  onPreviewClick(event) {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const card = dialog.querySelector('.sticker-preview__card');
    const rect = card?.getBoundingClientRect();
    if (!rect) return;

    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) setTimeout(() => dialog.close());
  }

  /**
   * Escape closes only the card; preventDefault keeps the cart drawer open.
   * @param {KeyboardEvent} event
   */
  onPreviewKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closePreview();
  }

  previousSlide() {
    this.#stepSlide(-1);
  }

  nextSlide() {
    this.#stepSlide(1);
  }

  /** @param {number} direction */
  #stepSlide(direction) {
    const slides = this.#openPreview?.querySelector('[data-slides]');
    if (!(slides instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slides.scrollBy({ left: direction * slides.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {HTMLDialogElement} dialog */
  #updateSlider(dialog) {
    const slides = dialog.querySelector('[data-slides]');
    if (!(slides instanceof HTMLElement) || slides.clientWidth === 0) return;

    const count = slides.children.length;
    const index = Math.round(slides.scrollLeft / slides.clientWidth);

    // aria-disabled (not disabled) keeps a focused arrow focusable at the ends
    const prev = dialog.querySelector('.sticker-preview__arrow--prev');
    const next = dialog.querySelector('.sticker-preview__arrow--next');
    prev?.setAttribute('aria-disabled', String(index <= 0));
    next?.setAttribute('aria-disabled', String(index >= count - 1));

    const counter = dialog.querySelector('[data-counter]');
    const visual = counter?.querySelector('[data-counter-visual]');
    if (visual) visual.textContent = `${index + 1} / ${count}`;
    const spoken = counter?.querySelector('[data-counter-sr]');
    const previous = this.#lastIndex.get(slides);
    this.#lastIndex.set(slides, index);
    if (previous === undefined || previous === index) return;
    if (spoken && counter instanceof HTMLElement && counter.dataset.template) {
      const text = counter.dataset.template.replace('[index]', String(index + 1));
      if (spoken.textContent !== text) spoken.textContent = text;
    }
  }

  /**
   * @param {number} variantId
   * @param {Event} event
   */
  async addPack(variantId, event) {
    if (this.#busy) return;
    this.#busy = true;

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const title =
      button?.closest('.sticker-addon__item, .sticker-preview')?.querySelector('.sticker-addon__title, .sticker-preview__title')
        ?.textContent?.trim() ?? '';
    button?.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    this.refs.error?.setAttribute('hidden', '');

    // Line changes use positional indexes; keep the cart quiet until the add has re-rendered it.
    const cartItems = this.closest('cart-items-component');
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    cartItems?.classList.add('cart-items-disabled');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
        })
      );
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);

      // The pack is in the cart from here on: never show the error or allow a second add.
      this.closePreview();
      button?.closest('.sticker-addon__item')?.setAttribute('hidden', '');
      CartStickerAddon.announce((this.dataset.addedText || '').replace('[title]', title));

      let cart;
      try {
        cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
          (cartResponse) => cartResponse.json()
        );
      } catch (cartError) {
        // Couldn't read the cart back: settle the event and re-render the cart sections directly.
        deferred.reject(cartError);
        for (const id of sectionIds) if (id) sectionRenderer.renderSection(id, { cache: false });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-sticker-addon',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      // The row disappears after the re-render; keep keyboard users on a stable control.
      deferred.promise.finally(() =>
        requestAnimationFrame(() => /** @type {HTMLElement | null} */ (document.getElementById('checkout'))?.focus())
      );
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'INVALID',
        })
      );
      // The error lives in the cart, not in the card; close the card so it can be seen.
      this.closePreview();
      this.refs.error?.removeAttribute('hidden');
      button?.removeAttribute('aria-disabled');
    } finally {
      button?.removeAttribute('aria-busy');
      cartItems?.classList.remove('cart-items-disabled');
      this.#busy = false;
    }
  }
}

if (!customElements.get('cart-sticker-addon')) {
  customElements.define('cart-sticker-addon', CartStickerAddon);
}
